import React, {useMemo, useState} from 'react';
import {Box, Button, Chip, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography} from "@mui/material";
import PropTypes from "prop-types";
import {
  formatPhoneNumber,
  parseFormattedPhoneNumber,
  validateName,
  validatePhoneNumber
} from "../../../util/ValidationUtils";
import {AccountBalanceWallet, Business, InfoOutlined, Person, Place} from "@mui/icons-material";
import AddressLookup from "../AddressLookup";
import {confirmTermsOfService, requestEmailVerificationToken, updateAccount} from "../../user/request/user-requests";
import {requestLoggedInAuthoritySource} from "../../../services/session/session-requests";
import Busy from "../../Busy";
import {checkAddressForConfirmation, createLocationFromAddress, getGoogleSuppliedAddressParts} from "../../../util/AddressUtil";
import AddressConfirmationDialog from "../../address/AddressConfirmationDialog";
import ChargeModelButton from '../ChargeModelButton';
import PayoutScheduleSelect from '../PayoutScheduleSelect';
import TermsAndPolicyCheckbox from "../../TermsAndPolicyCheckbox";
import UploadLegalAgreement from "../UploadLegalAgreement";
import {createTimestampedFileName, sanitizeFileName, validateFileWithErrorMessage} from "../../../util/FileUtils";
import {deleteReplacedListingImage, uploadCompanyProfileImage, uploadLegalAgreement} from "../../checkin/request/file-upload-requests";
import ViewLegalAgreement from "../ViewLegalAgreement";
import {isSupplierAccount} from "../../../util/AccountUtils";
import SupplierProfileUrl from "../SupplierProfileUrl";
import {useFeatureFlags} from "../../../hooks/routing/useFeatureFlags";
import {EditCompanyProfileSaveBar} from "./style";
import SettingsSection, {SettingsFieldGrid} from "../SettingsSection";
import {getErrorMessage} from "../../../util/NetworkErrorUtil";
import {INITIAL_SPECIAL_CHARS_REGEX} from "../../../util/sanitizeUtil";
import {useValidateByRegex} from '../../../hooks/validation/useValidateByRegex';
import {constructListingImageUrl} from "../../../util/ConstructImageUrlUtil";

import AspectRatioImageUpload from '../../imageUpload/AspectRatioImageUpload';
import AccountEmailChange from '../AccountEmailChange';
import {useSnackbar} from "../../../context/snackbar-context";

const COMPANY_EMAIL_TOOLTIP = "This is your company's main contact email. It can be different from the email you use to sign in, and changing it won't change your sign-in username.";

// Company logos are stored at 4:3, like location photos (#3487). The public profile header still shows the logo in a
// circular avatar, which trims the sides.
const COMPANY_LOGO_ASPECT_RATIO = 4 / 3;

const companyDescriptionsValidations = [
  {
    regexes: [
      INITIAL_SPECIAL_CHARS_REGEX,
    ],
    errorMessage: 'a special character at the beginning'
  },
  {
    regexes: [
      /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/i,
    ],
    errorMessage: 'email addresses.'
  },
  {
    regexes: [
      /\+?1?[-.]?\(?\d{3}\)?[-.\s]?\d{3}[-.]?\d{4}/i,
      /\+\d{1,3}[-\s.]?\(?\d{1,4}\)?[-\s.]?\d{1,4}[-\s.]?\d{1,4}(?:[-\s.]?\d{1,9})?/i,
      /\b\d{3}[-\s.]\d{3}[-\s.]\d{4}\b/i
    ],
    errorMessage: 'phone numbers.'
  },
  {
    regexes: [
      /(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|net|org|io|co|edu|gov|mil|biz|info|mobi|name|aero|asia|jobs|museum)(?:\/[^\s]*)?/i,
    ],
    errorMessage: 'URLs'
  },
  {
    regexes: [
      /@[A-Za-z0-9_]{2,30}/,
    ],
    errorMessage: 'social media handles'
  },
];

const formatCompanyDescriptionValidationFailures = (validationFailures) => {
  if (!validationFailures || validationFailures.length === 0) {
    return '';
  }
  return `Company Description cannot contain ${validationFailures.join(', ')}`;
}

// The account API rejects updates whose address is missing a city or a state,
// so both are enforced client-side to surface the problem inline instead of as
// a server error after submit.
const CITY_STATE_REQUIRED_MESSAGE = 'Address must include at least a city and state.';

const lookupAddressFromAccount = (account) => ({
  lookupLocation: createLocationFromAddress(account),
  addressLine1: account.addressLine1,
  addressLine2: account.addressLine2,
  city: account.city,
  state: account.state,
  zip: account.zip,
  addressLatitude: account.addressLatitude,
  addressLongitude: account.addressLongitude,
});

const EditCompanyProfile = ({ account, handleAccountUpdated }) => {
  const { supplierPublicProfile } = useFeatureFlags();
  const { snackbarShowMessage } = useSnackbar();
  const [companyToEdit, setCompanyToEdit] = useState(Object.assign({}, account));
  const [lookupAddress, setLookupAddress] = useState(() => lookupAddressFromAccount(account));
  // Error Messages
  const [companyNameErrorMsg, setCompanyNameErrorMsg] = useState('');
  const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState('');
  const [lookupAddressErrorMsg, setLookupAddressErrorMsg] = useState('');
  const [firstNameErrorMsg, setFirstNameErrorMsg] = useState('');
  const [lastNameErrorMsg, setLastNameErrorMsg] = useState('');
  const [legalAgreementErrorMsg, setLegalAgreementErrorMsg] = useState('');

  const [companyDescription, setCompanyDescription] = useState(account.companyDescription || '');
  const companyDescriptionErrors = useValidateByRegex({
    value: companyDescription,
    validations: companyDescriptionsValidations
  });

  const [useCalendarDays, setUseCalendarDays] = useState(account.useCalendarDays);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [legalAgreement, setLegalAgreement] = useState();

  // A newly chosen logo is uploaded on save, and the name the API assigns is persisted. Until then the saved name is
  // kept, or '' once the logo has been removed.
  const [pendingCompanyLogo, setPendingCompanyLogo] = useState(null);
  const [companyLogoFileName, setCompanyLogoFileName] = useState(account.listingImageFileName);
  // True while a newly chosen logo is still being cropped to 4:3; saving then would upload the uncropped image.
  const [isPreparingCompanyLogo, setIsPreparingCompanyLogo] = useState(false);
  // Stable array identities keep the uploader from recreating its preview URLs on every render.
  const pendingCompanyLogoFiles = useMemo(() => (pendingCompanyLogo ? [pendingCompanyLogo] : []), [pendingCompanyLogo]);
  const savedCompanyLogos = useMemo(
    () => (companyLogoFileName
      ? [{fileName: companyLogoFileName, url: constructListingImageUrl(companyLogoFileName)}]
      : []),
    [companyLogoFileName]
  );

  const [emailSent, setEmailSent] = useState(false);

  // Holds the typed address and Google's suggested match while the user is
  // being asked to confirm which one to save. Null when no confirmation is pending.
  const [pendingAddressConfirmation, setPendingAddressConfirmation] = useState(null);

  // Builds the AddressLookup state shape from a geocoded address returned by
  // checkAddressForConfirmation. `lookupLocationOverride` lets the caller keep
  // the user's own typed text as the displayed address while still applying
  // Google's resolved address parts/coordinates.
  const buildLookupAddressFromGeocodedAddress = (geocodedAddress, lookupLocationOverride) => ({
    lookupLocation: lookupLocationOverride ?? geocodedAddress.formattedAddress,
    addressLine1: geocodedAddress.addressLine1,
    addressLine2: geocodedAddress.addressLine2,
    city: geocodedAddress.city,
    state: geocodedAddress.state,
    zip: geocodedAddress.zip,
    addressLatitude: geocodedAddress.addressLatitude,
    addressLongitude: geocodedAddress.addressLongitude,
  });

  const simpleTextValidation = (value, label) => {
    if (!value || !value.trim()) {
      return `Please enter a ${label}`;
    }
    if (INITIAL_SPECIAL_CHARS_REGEX.test(value)) {
      return `${label} cannot start with a special character`;
    }
    return '';
  };

  const simpleNameValidation = (value, label) => {
    if (!value || !value.trim()) {
      return `Please enter a ${label}`;
    }
    return validateName(value) ? '' : `${label} can contain only spaces, hyphens, and alphanumeric characters`;
  };

  const phoneNumberValidation = (value) => {
    return validatePhoneNumber(value) ? '' : 'Phone number must be 10 digits';
  };

  // In this single-image uploader a newly chosen (or re-cropped) logo supersedes the saved one; removing it brings the
  // saved logo back.
  const handleCompanyLogoFilesChange = (files) => {
    setPendingCompanyLogo(files[0] ?? null);
  };

  const handleRemoveSavedCompanyLogo = () => {
    setCompanyLogoFileName('');
  };

  const handleSimpleTextChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    switch (name) {
      case 'companyName':
        setCompanyToEdit({ ...companyToEdit, companyName: value });
        setCompanyNameErrorMsg(simpleTextValidation(value, 'Company Name'));
        break;
      case 'phoneNumber':
        setCompanyToEdit({ ...companyToEdit, phoneNumber: parseFormattedPhoneNumber(value) });
        setPhoneNumberErrorMsg(phoneNumberValidation(value.replace(/[^0-9]*/g,'')));
        break;
      case 'companyDescription':
        setCompanyDescription(value);
        setCompanyToEdit({ ...companyToEdit, companyDescription: value });
        break;
      case 'firstName':
        setCompanyToEdit({ ...companyToEdit, firstName: value });
        setFirstNameErrorMsg(simpleNameValidation(value, 'First Name'));
        break;
      case 'lastName':
        setCompanyToEdit({ ...companyToEdit, lastName: value });
        setLastNameErrorMsg(simpleNameValidation(value, 'Last Name'));
        break;
      default:
        break;
    }
  };

  const handleAddressChange = (lookupAddressObject) => {
    // If the user selects a location then clear the error message
    // See validation in form submit handler
    if (lookupAddressObject.lookupLocation && (lookupAddressObject.addressLatitude || lookupAddressObject.addressLongitude)) {
      setLookupAddressErrorMsg('');
    }
    // Clear lat and lng every time the user types in the lookupLocation field as they invalidate the lat and lng.
    // Once the user selects an address from the lookup, then the received lat and lng will be valid.
    setLookupAddress({
      ...lookupAddressObject,
      addressLatitude: lookupAddressObject.addressLatitude || null,
      addressLongitude: lookupAddressObject.addressLongitude || null,
    });
  };

  const handleChangePayoutSchedule = (e) => {
    setCompanyToEdit({ ...companyToEdit, payoutSchedule: e.target.value })
  }

  const handleChangeUseCalendarDays = (value) => {
    setUseCalendarDays(value);
  }

  const handleTermsCheckbox = (e) => {
    setHasAcceptedTerms(e.target.checked);
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setLegalAgreement(null);
      setLegalAgreementErrorMsg('');
      return;
    }
    const newName = sanitizeFileName(file.name);
    const newFile = new File([file], newName, { type: file.type });
    const validationMessage = validateFileWithErrorMessage(newFile);
    if (validationMessage) {
      setLegalAgreementErrorMsg(validationMessage);
      return;
    }
    setLegalAgreement(newFile);
    setLegalAgreementErrorMsg('');
  };

  // Performs the actual save: uploads a new legal agreement and company logo
  // first, then confirms the Terms of Service and updates the account with the
  // file names the API returned, and only then removes a replaced logo. Takes
  // the resolved address explicitly rather than reading `lookupAddress` state
  // directly, since this may be invoked immediately after resolving a manually
  // typed address (silent match or address-confirmation dialog) before that
  // state update has been committed/re-rendered.
  const saveCompanyProfile = async (resolvedLookupAddress, addressUserAsserted) => {
    const handleResponseError = (error, message = 'An error occurred while updating your account. Please contact support if this issue persists.', duration = 10000) => {
      snackbarShowMessage(getErrorMessage(error, message), 'error', duration);
      setHasAcceptedTerms(false);
    }
    const handleUploadError = (message) => {
      snackbarShowMessage(message, 'error', 8000);
      setHasAcceptedTerms(false);
    }

    Busy.set(true);
    try {
      let supplierLegalAgreementFileName = account.supplierLegalAgreementFileName;
      if (legalAgreement) {
        try {
          const uploadedName = await uploadLegalAgreement(legalAgreement, legalAgreement.name, legalAgreement.type);
          supplierLegalAgreementFileName = `${account.id}/${uploadedName}`;
        } catch (_error) {
          handleUploadError('Legal Agreement failed to upload, please try again');
          return;
        }
      }

      let listingImageFileName = companyLogoFileName;
      if (pendingCompanyLogo) {
        try {
          listingImageFileName = await uploadCompanyProfileImage(
            pendingCompanyLogo,
            createTimestampedFileName(sanitizeFileName(pendingCompanyLogo.name)),
            pendingCompanyLogo.type
          );
        } catch (_error) {
          handleUploadError('Company logo failed to upload, please try again');
          return;
        }
      }

      const body = {
        addressLatitude: resolvedLookupAddress.addressLatitude,
        addressLine1: resolvedLookupAddress.addressLine1,
        addressLine2: resolvedLookupAddress.addressLine2,
        addressLongitude: resolvedLookupAddress.addressLongitude,
        city: resolvedLookupAddress.city,
        companyDescription: companyToEdit.companyDescription?.trim() ?? null,
        companyName: companyToEdit.companyName.trim(),
        email: account.email,
        firstName: companyToEdit.firstName.trim(),
        id: account.id,
        lastName: companyToEdit.lastName.trim(),
        listingImageFileName,
        payoutSchedule: companyToEdit.payoutSchedule,
        phoneNumber: companyToEdit.phoneNumber,
        state: resolvedLookupAddress.state,
        supplierLegalAgreementFileName,
        type: account.type,
        useCalendarDays: useCalendarDays,
        username: account.username,
        zip: resolvedLookupAddress.zip,
        addressUserAsserted,
      };

      const tosAgreedAccount = {
        companyName: body.companyName,
        firstName: body.firstName,
        lastName: body.lastName,
        phoneNumber: body.phoneNumber,
        email: body.email,
        password: "",
        confirmPassword: ""
      }

      try {
        await confirmTermsOfService(account.id, tosAgreedAccount);
      } catch (error) {
        handleResponseError(error, 'Terms Of Service failed to upload, please try again.', 8000);
        return;
      }

      try {
        await updateAccount(body);
      } catch (error) {
        handleResponseError(error);
        return;
      }

      setLegalAgreement(null);
      setLegalAgreementErrorMsg('');
      setPendingCompanyLogo(null);
      setCompanyLogoFileName(listingImageFileName);
      if (account.listingImageFileName && account.listingImageFileName !== listingImageFileName) {
        deleteReplacedListingImage(account.listingImageFileName);
      }

      try {
        const resp = await requestLoggedInAuthoritySource();
        handleAccountUpdated(resp.body);
        // The API normalises what was sent (trimmed text, a rebuilt address), so restart editing from what it saved;
        // otherwise those differences would still count as unsaved changes.
        const savedAccount = resp.body;
        setCompanyToEdit({ ...savedAccount });
        setCompanyDescription(savedAccount.companyDescription || '');
        setLookupAddress(lookupAddressFromAccount(savedAccount));
        setUseCalendarDays(savedAccount.useCalendarDays);
        snackbarShowMessage('Account updated successfully', 'success', 8000);
      } catch (error) {
        handleResponseError(error);
      } finally {
        setHasAcceptedTerms(false);
      }
    } finally {
      Busy.set(false);
    }
  };

  // Fields that differ from the saved account, for the save bar's "N unsaved changes".
  const differs = (edited, saved) => (edited ?? '') !== (saved ?? '');
  const unsavedChangeCount = [
    differs(companyToEdit.companyName, account.companyName),
    differs(companyToEdit.companyDescription, account.companyDescription),
    differs(companyToEdit.firstName, account.firstName),
    differs(companyToEdit.lastName, account.lastName),
    differs(companyToEdit.phoneNumber, account.phoneNumber),
    differs(companyToEdit.payoutSchedule, account.payoutSchedule),
    differs(lookupAddress.lookupLocation, createLocationFromAddress(account)),
    !!useCalendarDays !== !!account.useCalendarDays,
    !!pendingCompanyLogo || differs(companyLogoFileName, account.listingImageFileName),
    !!legalAgreement,
  ].filter(Boolean).length;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (
      isPreparingCompanyLogo ||
      companyNameErrorMsg ||
      phoneNumberErrorMsg ||
      companyDescriptionErrors.length > 0 ||
      firstNameErrorMsg ||
      lastNameErrorMsg ||
      legalAgreementErrorMsg
    ) {
      return;
    }

    // Address was selected from the Google Places dropdown, so lat/lng are
    // already trustworthy — no manual-entry confirmation needed. The dropdown
    // can still return a broader place than we accept (e.g. picking a state or
    // country suggestion yields coordinates but no city), so the city + state
    // minimum is enforced here too rather than letting the API reject it.
    const addressSelectedFromDropdown = !!(lookupAddress.addressLatitude && lookupAddress.addressLongitude);
    if (addressSelectedFromDropdown) {
      if (!lookupAddress.city || !lookupAddress.state) {
        setLookupAddressErrorMsg(CITY_STATE_REQUIRED_MESSAGE);
        return;
      }
      saveCompanyProfile(lookupAddress, false);
      return;
    }

    if (!lookupAddress.lookupLocation) {
      setLookupAddressErrorMsg('Please select an address from the drop down menu');
      return;
    }

    // Address was typed manually rather than selected from the dropdown —
    // verify it with Google before saving instead of hard-blocking Save.
    // Company Details accepts a City + State-level address as the minimum
    // (unlike the Create/Edit Location wizard, which requires a full street
    // address for an accurate pin location).
    Busy.set(true);
    try {
      const confirmationCheckResult = await checkAddressForConfirmation(lookupAddress.lookupLocation, 'cityState');

      if (confirmationCheckResult.status === 'not-found') {
        setLookupAddressErrorMsg("We couldn't find that address. Please check for typos or select a suggestion from the dropdown.");
        return;
      }

      if (confirmationCheckResult.status === 'incomplete') {
        setLookupAddressErrorMsg(CITY_STATE_REQUIRED_MESSAGE);
        return;
      }

      if (confirmationCheckResult.status === 'match') {
        // Typed address is essentially identical to Google's top suggestion —
        // apply it silently and proceed with save, no modal needed.
        const resolvedLookupAddress = buildLookupAddressFromGeocodedAddress(confirmationCheckResult.address);
        setLookupAddress(resolvedLookupAddress);
        setLookupAddressErrorMsg('');
        await saveCompanyProfile(resolvedLookupAddress, false);
        return;
      }

      // status === 'needs-confirmation': pause submit and ask the user to
      // pick between what they typed and Google's suggestion.
      setPendingAddressConfirmation({
        typedAddress: lookupAddress.lookupLocation,
        geocodedAddress: confirmationCheckResult.address,
      });
    } finally {
      Busy.set(false);
    }
  };

  const handleAddressConfirmationCancel = () => {
    // User canceled — do not save, leave the form as-is so they can keep editing.
    setPendingAddressConfirmation(null);
  };

  const handleAddressConfirmationConfirm = (selection) => {
    if (!pendingAddressConfirmation) {
      return;
    }
    const { typedAddress, geocodedAddress } = pendingAddressConfirmation;
    const userKeptTypedAddress = selection === 'typed';
    const resolvedLookupAddress = buildLookupAddressFromGeocodedAddress(
      geocodedAddress,
      userKeptTypedAddress ? typedAddress : undefined
    );

    setLookupAddress(resolvedLookupAddress);
    setLookupAddressErrorMsg('');
    setPendingAddressConfirmation(null);
    saveCompanyProfile(resolvedLookupAddress, userKeptTypedAddress);
  };

  const handleVerificationEmailRequest = () => {
    Busy.set(true);
    requestEmailVerificationToken()
    .then(() => {
      setEmailSent(true);
      snackbarShowMessage('Verification email sent successfully', 'success', 8000);
    }).catch((error) => {
      snackbarShowMessage(getErrorMessage(error), 'error', 10000);
    }).finally(() => {
      Busy.set(false);
    });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className={'w-100'}>
      <Stack gap={'1.71rem'}>
        <SettingsSection
          title={'Company Profile'}
          description={'Your company name and how it appears to other SecurSpace users.'}
          icon={<Business/>}
        >
          <SettingsFieldGrid>
            <TextField
              variant='standard'
              value={companyToEdit.companyName}
              label={'Company Name'}
              name={'companyName'}
              onChange={handleSimpleTextChange}
              error={!!companyNameErrorMsg}
              helperText={companyNameErrorMsg ? companyNameErrorMsg : ''}
              placeholder={'Please enter a company name'}
              required
              fullWidth
            />
            {isSupplierAccount(account) && account.profileUrl && supplierPublicProfile &&
              <SupplierProfileUrl profileUrl={account.profileUrl} />
            }
          </SettingsFieldGrid>
          <TextField
            variant='standard'
            value={companyToEdit.companyDescription || ''}
            label={'Company Description'}
            name={'companyDescription'}
            onChange={handleSimpleTextChange}
            placeholder={'Please enter a company description'}
            error={!!(companyDescriptionErrors && companyDescriptionErrors.length)}
            helperText={formatCompanyDescriptionValidationFailures(companyDescriptionErrors)  || 'Describe your company in a few words'}
            multiline
            fullWidth
          />
          {
            isSupplierAccount(account) && <Stack gap={'0.57rem'}>
              <Typography variant={'caption'} color={'textSecondary'}>Company Logo</Typography>
              <AspectRatioImageUpload
                multiple={false}
                aspectRatio={COMPANY_LOGO_ASPECT_RATIO}
                files={pendingCompanyLogoFiles}
                onFilesChange={handleCompanyLogoFilesChange}
                existingImages={savedCompanyLogos}
                onRemoveExisting={handleRemoveSavedCompanyLogo}
                onPreparingChange={setIsPreparingCompanyLogo}
              />
              <Typography variant={'caption'} color={'textSecondary'}>
                New images are cropped to a 4:3 ratio.
              </Typography>
            </Stack>
          }
        </SettingsSection>

        <SettingsSection
          title={'Contact Information'}
          description={'The primary contact for this account.'}
          icon={<Person/>}
        >
          <SettingsFieldGrid>
            <TextField
              variant='standard'
              value={companyToEdit.firstName}
              label={'First Name'}
              name={'firstName'}
              onChange={handleSimpleTextChange}
              error={!!firstNameErrorMsg}
              helperText={firstNameErrorMsg || ''}
              placeholder={'Please enter your first name'}
              required
              fullWidth
            />
            <TextField
              variant='standard'
              value={companyToEdit.lastName}
              label={'Last Name'}
              name={'lastName'}
              onChange={handleSimpleTextChange}
              error={!!lastNameErrorMsg}
              helperText={lastNameErrorMsg || ''}
              placeholder={'Please enter your last name'}
              required
              fullWidth
            />
            <Stack gap={'0.43rem'} alignItems={'flex-start'}>
              <TextField
                variant='standard'
                value={account.email}
                label={'Company Email'}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position='end'>
                      {!account.emailVerified && <Chip label={'Unverified'} color={'warning'} size={'small'} variant={'outlined'} />}
                      <Tooltip
                        title={COMPANY_EMAIL_TOOLTIP}
                        arrow
                        describeChild
                        enterTouchDelay={0}
                        slotProps={{tooltip: {sx: {typography: 'body2'}}}}
                      >
                        <IconButton aria-label='About company email' edge='end' size='small'>
                          <InfoOutlined fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              { !account.emailVerified &&
                <Button
                  onClick={handleVerificationEmailRequest}
                  disabled={emailSent}
                >
                  Resend Verification Email
                </Button>
              }
              <AccountEmailChange key={`${account.id}:${account.loggedInUsername}`} account={account}/>
            </Stack>
            <TextField
              variant='standard'
              value={formatPhoneNumber(companyToEdit.phoneNumber)}
              label={'Phone Number'}
              name={'phoneNumber'}
              type={'tel'}
              onChange={handleSimpleTextChange}
              error={!!phoneNumberErrorMsg}
              helperText={phoneNumberErrorMsg || ''}
              placeholder={'Please enter a phone number'}
              required
              fullWidth
            />
          </SettingsFieldGrid>
        </SettingsSection>

        <SettingsSection
          title={'Company Address'}
          description={'Where your company is based.'}
          icon={<Place/>}
        >
          <Box>
            <AddressLookup
              name={'address'}
              value={lookupAddress.lookupLocation}
              label={'Address'}
              placeholder={'Please enter your company address'}
              onChange={handleAddressChange}
              error={!!lookupAddressErrorMsg}
              helperText={lookupAddressErrorMsg}
            />
          </Box>
        </SettingsSection>

        {
          isSupplierAccount(account) && <SettingsSection
            title={'Payouts & Billing'}
            description={'How bookings are charged and when you get paid.'}
            icon={<AccountBalanceWallet/>}
          >
            <SettingsFieldGrid>
              <PayoutScheduleSelect selection={companyToEdit.payoutSchedule} handleChangeSelection={handleChangePayoutSchedule} />
            </SettingsFieldGrid>
            {/* A full row: its two buttons have a minimum width that a half-width grid cell can't always hold. */}
            <ChargeModelButton useCalendarDays={useCalendarDays} setUseCalendarDays={handleChangeUseCalendarDays} />
            <Stack gap={'0.43rem'} alignItems={'flex-start'}>
              <UploadLegalAgreement handleFileUpload={handleFileUpload} error={!!legalAgreementErrorMsg} helperText={legalAgreementErrorMsg || ''} />
              { !legalAgreementErrorMsg && <ViewLegalAgreement account={account} legalAgreementFile={legalAgreement} /> }
            </Stack>
          </SettingsSection>
        }

        <EditCompanyProfileSaveBar variant={'outlined'}>
          <Box sx={{flex: '1 1 26rem', minWidth: 0}}>
            <TermsAndPolicyCheckbox
              checked={hasAcceptedTerms}
              onChange={handleTermsCheckbox}
              name={"agreementAccepted"}
              accountType={account.type}
              disabled={unsavedChangeCount === 0}
              inline
            />
          </Box>
          <Stack direction={'row'} alignItems={'center'} gap={'0.86rem'}>
            <Typography variant={'body2'} color={'textSecondary'} role={'status'}>
              {unsavedChangeCount === 0
                ? 'No unsaved changes'
                : `${unsavedChangeCount} unsaved change${unsavedChangeCount === 1 ? '' : 's'}`}
            </Typography>
            <Button
              type={'submit'}
              variant={'contained'}
              color={'secondary'}
              disabled={!hasAcceptedTerms || unsavedChangeCount === 0 || isPreparingCompanyLogo}
            >
              Save Changes
            </Button>
          </Stack>
        </EditCompanyProfileSaveBar>
      </Stack>
      <AddressConfirmationDialog
        open={!!pendingAddressConfirmation}
        typedAddress={pendingAddressConfirmation?.typedAddress || ''}
        suggestedAddress={pendingAddressConfirmation?.geocodedAddress?.formattedAddress || ''}
        googleSuppliedParts={
          pendingAddressConfirmation?.geocodedAddress
            ? getGoogleSuppliedAddressParts(
                pendingAddressConfirmation.typedAddress || '',
                pendingAddressConfirmation.geocodedAddress,
              )
            : []
        }
        onClose={handleAddressConfirmationCancel}
        onConfirm={handleAddressConfirmationConfirm}
      />
    </form>
  );
};

EditCompanyProfile.propTypes = {
  account: PropTypes.object.isRequired,
  handleAccountUpdated: PropTypes.func.isRequired,
};

export default EditCompanyProfile;
